import React from "react";
import HoverTooltip from "./HoverTooltip";

const getDisplayFrequencyLabel = (frequency) => {
  const normalized = String(frequency || "Yearly")
    .trim()
    .toLowerCase();
  if (normalized === "monthly") return "Month";
  if (normalized === "quarterly" || normalized === "quaterly") return "Quarter";
  if (normalized === "half-yearly" || normalized === "half yearly")
    return "Half Year";
  return "Year";
};

/**
 * Reusable Rider/Add-on card component.
 * @param {'dropdown' | 'text' | 'readonly'} coverEntry - How cover amount is captured (default dropdown).
 */
const RiderCard = ({
  rider,
  description,
  onKnowMore,
  isSelected,
  riderPremium,
  premiumFrequency = "Yearly",
  defaultPremiumLabel = "Starts @ ₹22",
  coverValue,
  coverOptions = [],
  onToggle,
  onCoverChange,
  coverEntry = "dropdown",
  /** Raw string for text entry (debounced commit in parent). */
  coverInputValue = "",
  coverError = "",
  onCoverInputChange,
  onCoverInputBlur,
  coverPlaceholder = "e.g. 500000 or 5 lakh",
  /** When true, the Add/Added control is non-interactive (e.g. mutually exclusive with ECBO). */
  toggleDisabled = false,
  toggleDisabledTitle = "",
}) => {
  const isPlusRider = rider.id === "accidental-death-disability-plus";
  const hasActualPremium = riderPremium != null && riderPremium !== "";
  const premiumLabel = hasActualPremium
    ? `₹${Number(riderPremium).toLocaleString("en-IN")}`
    : defaultPremiumLabel;
  const frequencyLabel = getDisplayFrequencyLabel(premiumFrequency);
  const showFrequency = hasActualPremium;
  const readonlyCover = coverEntry === "readonly" || isPlusRider;

  const actionButton = (
    <button
      type="button"
      className={`rider-action-button btn secondary-button small ${isSelected ? "added" : ""}${toggleDisabled ? " rider-action-button--disabled" : ""}`}
      disabled={toggleDisabled || coverError}
      onClick={() => {
        if (toggleDisabled || coverError) return;
        onToggle(rider.id);
      }}
    >
      {isSelected ? (
        <>
          <span>Added</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="8" fill="#97262A" />
            <path
              d="M10.9068 5.59364C10.7898 5.4766 10.6002 5.4766 10.4835 5.59364L6.50329 9.57382L5.51698 8.58818C5.40027 8.47114 5.21034 8.47114 5.09364 8.58818C4.9766 8.70521 4.9766 8.89481 5.09364 9.01152L6.25415 10.172C6.28495 10.2028 6.32123 10.2244 6.35955 10.2391C6.40713 10.2682 6.4588 10.2877 6.51253 10.2877C6.58918 10.2877 6.66585 10.2586 6.72437 10.2001L10.9068 6.01733C11.0238 5.90029 11.0238 5.71069 10.9068 5.59366V5.59364H10.9068Z"
              fill="white"
            />
          </svg>
        </>
      ) : (
        <>
          <span>Add</span>
          <svg
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.5 6.25C12.5 6.44891 12.421 6.63968 12.2803 6.78033C12.1397 6.92098 11.9489 7 11.75 7H7V11.75C7 11.9489 6.92098 12.1397 6.78033 12.2803C6.63968 12.421 6.44891 12.5 6.25 12.5C6.05109 12.5 5.86032 12.421 5.71967 12.2803C5.57902 12.1397 5.5 11.9489 5.5 11.75V7H0.75C0.551088 7 0.360322 6.92098 0.21967 6.78033C0.0790177 6.63968 0 6.44891 0 6.25C0 6.05109 0.0790177 5.86032 0.21967 5.71967C0.360322 5.57902 0.551088 5.5 0.75 5.5H5.5V0.75C5.5 0.551088 5.57902 0.360322 5.71967 0.21967C5.86032 0.0790177 6.05109 0 6.25 0C6.44891 0 6.63968 0.0790177 6.78033 0.21967C6.92098 0.360322 7 0.551088 7 0.75V5.5H11.75C11.9489 5.5 12.1397 5.57902 12.2803 5.71967C12.421 5.86032 12.5 6.05109 12.5 6.25Z"
              fill="#97262A"
            />
          </svg>
        </>
      )}
    </button>
  );

  return (
    <div className="rider-card">
      <div className="rider-header">
        <h6 className="rider-title m-0">{rider.name}</h6>
        {
          isSelected && (
            <span className="rider-premium">
              <span className="text-bold">
                {hasActualPremium ? "@ " : ""}
                {premiumLabel}
              </span>
              {showFrequency ? ` / ${frequencyLabel}` : ""}
            </span>
          )
        }
        
      </div>
      <div className="rider-description">
        <p>
          <span>{description}</span>
          <button
            type="button"
            className="know-more-link-inline btn tertiary-button small p-0"
            onClick={() => onKnowMore?.(rider)}
          >
            {" "}
            Know More{" "}
          </button>
        </p>
      </div>
      <div className="rider-cover-footer">
        {(isPlusRider || !isSelected) ? (
          <div className="rider-cover-spacer" aria-hidden="true" />
        ) : (
          <div className="rider-cover-selector">
            <label htmlFor={`rider-cover-${rider.id}`}>Cover</label>
            {readonlyCover ? (
              <input
                id={`rider-cover-${rider.id}`}
                type="text"
                className="rider-cover-dropdown"
                autoFocus
                value={
                  coverValue
                    ? `₹ ${Number(coverValue).toLocaleString("en-IN")}`
                    : ""
                }
                readOnly
                disabled
                aria-label={`${rider.name} cover matches life cover`}
              />
            ) : coverEntry === "text" ? (
              <>
                <input
                  id={`rider-cover-${rider.id}`}
                  type="text"
                  className="rider-cover-dropdown"
                  autoFocus
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder={coverPlaceholder}
                  value={coverInputValue}
                  onChange={(e) => onCoverInputChange?.(rider.id, e.target.value) }
                  onBlur={() => onCoverInputBlur?.(rider.id)}
                  aria-invalid={Boolean(coverError)}
                  aria-describedby={
                    coverError ? `rider-cover-err-${rider.id}` : undefined
                  }
                />
                
              </>
            ) : (
              <select
                id={`rider-cover-${rider.id}`}
                className="rider-cover-dropdown"
                value={coverValue}
                onChange={(e) => onCoverChange(rider.id, e.target.value)}
              >
                {coverOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        {toggleDisabled && toggleDisabledTitle  ? (
          <HoverTooltip content={toggleDisabledTitle} placement="top">
            {actionButton}
          </HoverTooltip>
        ) : (
          actionButton
        )}
      </div>
      {coverError ? (
        <div
          id={`rider-cover-err-${rider.id}`}
          className="rider-cover-error small text-danger mt-1"
          role="alert"
        >
          {coverError}
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(RiderCard);
